import React, { useEffect, useState } from "react";
import { App, Button, Card, Col, Flex, Row, Switch, Typography } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import {
  getInstitutionModuleSettingsApi,
  saveInstitutionModuleSettingsApi,
} from "../../../apiservice/setting";
import { moduleRegistry } from "../../../lib/moduleRegistry";

const { Title, Text } = Typography;

const ModuleAccessManage = () => {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const [disabledModules, setDisabledModules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchSettings = () => {
    setLoading(true);
    getInstitutionModuleSettingsApi()
      .then((res) => {
        setDisabledModules(res.data?.disabledModules || []);
      })
      .catch(() => {
        message.error("Failed to load module access settings");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const isEnabled = (value) => !disabledModules.includes(value);

  const toggleModule = (value, checked) => {
    setDisabledModules((prev) =>
      checked ? prev.filter((key) => key !== value) : [...prev, value]
    );
  };

  const handleSave = () => {
    setSaving(true);
    saveInstitutionModuleSettingsApi({ disabledModules })
      .then(() => {
        message.success("Module access settings saved");
      })
      .catch(() => {
        message.error("Failed to save module access settings");
      })
      .finally(() => setSaving(false));
  };

  const handleCancel = () => {
    fetchSettings();
  };

  return (
    <div className="p-4">
      <App>
        <Card
          className="shadow-md rounded-xl mb-4"
          title={
            <div style={{ display: "flex", alignItems: "center" }}>
              <Button
                type="text"
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate("/settings")}
                style={{ marginRight: 8 }}
              />
              <Title level={4} style={{ margin: 0 }}>
                Module Access
              </Title>
            </div>
          }
          extra={
            <Flex gap={8}>
              <Button onClick={handleCancel} disabled={loading || saving}>
                Cancel
              </Button>
              <Button type="primary" loading={saving} onClick={handleSave} disabled={loading}>
                Save
              </Button>
            </Flex>
          }
          styles={{
            header: { borderBottom: "none", padding: "12px 16px" },
            body: { padding: 0 },
          }}
        />

        <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
          Turn a module off to remove it from the sidebar for everyone at this
          institution, even if their role would otherwise grant it. Changes take
          effect only after each user logs out and logs back in.
        </Text>

        {moduleRegistry.map((mod) => (
          <Card key={mod.value} className="shadow-md rounded-xl mb-3" loading={loading}>
            <Row justify="space-between" align="middle">
              <Col>
                <Text strong>{mod.label}</Text>
              </Col>
              <Col>
                <Switch
                  checked={isEnabled(mod.value)}
                  onChange={(checked) => toggleModule(mod.value, checked)}
                />
              </Col>
            </Row>

            {mod.children && mod.children.length > 0 && (
              <Row gutter={[16, 12]} style={{ marginTop: 16 }}>
                {mod.children.map((child) => (
                  <Col xs={24} sm={12} md={8} key={child.value}>
                    <Flex justify="space-between" align="center">
                      <Switch
                        size="small"
                        checked={isEnabled(child.value)}
                        onChange={(checked) => toggleModule(child.value, checked)}
                      />
                      <Text>{child.label}</Text>
                    </Flex>
                  </Col>
                ))}
              </Row>
            )}
          </Card>
        ))}
      </App>
    </div>
  );
};

export default ModuleAccessManage;
